import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import vouchersApi from '../services/vouchersApi';
import pageCache from '../services/pageCache';
import './VouchersPage.css';

// ─── Fluxo Principal ───
// Todo voucher nasce do chat. Cotação → Won → Voucher.
// O botão + Voucher no ChatWindow navega para /app/vouchers?contact_id=<id>
// A VouchersPage detecta o contact_id, verifica cotação Won e abre o fluxo.

export default function VouchersPage() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNewFlow, setShowNewFlow] = useState(false);
  const [initialContact, setInitialContact] = useState(null);
  const [initialWonQuote, setInitialWonQuote] = useState(null);
  const [wonQuoteError, setWonQuoteError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Ao montar: se veio do chat com contact_id, verifica cotação Won e abre fluxo
  useEffect(() => {
    const contactId = searchParams.get('contact_id');
    if (!contactId) return;

    (async () => {
      const [contact, wonQuote] = await Promise.all([
        vouchersApi.getContactById(contactId),
        vouchersApi.getWonQuote(contactId),
      ]);

      if (!wonQuote) {
        setWonQuoteError(contact?.name || 'Este cliente');
        return;
      }

      setInitialContact(contact);
      setInitialWonQuote(wonQuote);

      // Se já existe pacote vinculado à cotação Won → append
      const existingPackage = await vouchersApi.getPackageByQuoteId(wonQuote.quote_id);
      if (existingPackage) {
        setAddingToPackageId(existingPackage.id);
      } else {
        setShowNewFlow(true);
      }

      // Limpa params da URL para não reabrir ao navegar
      setSearchParams({}, { replace: true });
    })();
  }, []);

  const loadPackages = useCallback(async (skipCache = false) => {
    const CACHE_KEY = 'vouchers/packages';

    // Stale-while-revalidate: mostra cache imediatamente, revalida em background.
    // skipCache=true é usado por refreshSelected (precisa de dados frescos sempre).
    if (!skipCache) {
      const cached = pageCache.get(CACHE_KEY);
      if (cached) {
        setPackages(cached);
        setLoading(false);
        // Revalida em background — sem loading visível
        vouchersApi.getPackages().then(data => {
          pageCache.set(CACHE_KEY, data);
          setPackages(data);
        }).catch(err => console.error('Revalidação de vouchers falhou:', err));
        return;
      }
    }

    setLoading(true);
    try {
      const data = await vouchersApi.getPackages();
      pageCache.set(CACHE_KEY, data);
      setPackages(data);
    } catch (err) {
      console.error('Erro ao carregar pacotes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const refreshSelected = async () => {
    const data = await vouchersApi.getPackages();
    pageCache.set('vouchers/packages', data);
    setPackages(data);
    const updated = data.find(p => p.id === selected?.id);
    if (updated) setSelected(updated);
  };

  const handlePublish = async () => {
    if (!selected) return;
    setPublishing(true);
    setPublishedUrl(null);
    try {
      const result = await vouchersApi.publishRoteiro(selected.id);
      if (result.success) {
        setPublishedUrl(result.url);
        await refreshSelected();
      } else {
        alert('Erro ao publicar: ' + (result.error || 'tente novamente'));
      }
    } catch(e) {
      alert('Erro ao publicar: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyLink = () => {
    if (!publishedUrl) return;
    navigator.clipboard.writeText(publishedUrl);
  };

  const handleNewCreated = async (pkgId) => {
    const data = await vouchersApi.getPackages();
    setPackages(data);
    const created = data.find(p => p.id === pkgId);
    if (created) setSelected(created);
    setShowNewFlow(false);
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Excluir ${doc.file_name}?`)) return;
    await vouchersApi.deleteDocument(doc.id, doc.file_path);
    await refreshSelected();
  };

  const handleDeleteTimelineItem = async (item) => {
    if (!window.confirm(`Excluir "${item.title}"?`)) return;
    await vouchersApi.deleteTimelineItem(item.id);
    await refreshSelected();
  };

  const [addingToPackageId, setAddingToPackageId] = useState(null);

  const statusLabel = (s) => ({ draft: 'Rascunho', published: 'Publicado', active: 'Em Viagem', completed: 'Concluído', cancelled: 'Cancelado' }[s] || s);
  const statusClass = (s) => ({ draft: 'badge-draft', published: 'badge-published', active: 'badge-active', completed: 'badge-completed' }[s] || 'badge-draft');

  const contactDisplay = (pkg) => {
    const t = pkg.traveler, c = pkg.contact;
    if (t) return { name: t.full_name, email: t.email, phone: t.phone };
    if (c) return { name: c.name, email: c.email, phone: c.phone };
    return null;
  };

  return (
    <div className={`vouchers-page ${selected ? 'has-selection' : ''}`}>
      {/* Lista */}
      <div className="vouchers-list">
        <div className="vouchers-list-header">
          <h2>Vouchers</h2>
        </div>
        <div className="vouchers-list-items">
          {loading && <div className="empty-state"><div className="empty-state-hint">Carregando...</div></div>}
          {!loading && packages.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-text">Nenhum roteiro</div>
              <div className="empty-state-hint">Crie o primeiro para começar</div>
            </div>
          )}
          {packages.map(pkg => {
            const cd = contactDisplay(pkg);
            return (
              <div key={pkg.id} className={`package-card ${selected?.id === pkg.id ? 'active' : ''}`} onClick={() => { setSelected(pkg); setPublishedUrl(null); }}>
                <div className="package-card-title">{pkg.title}</div>
                {cd ? (
                  <div className="package-card-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {pkg.contact?.avatar_url && <img src={pkg.contact.avatar_url} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />}
                    {cd.name}
                  </div>
                ) : (
                  <div className="package-card-subtitle">{pkg.subtitle || 'Sem cliente'}</div>
                )}
                <div className="package-card-meta">
                  <span className={`package-badge ${statusClass(pkg.status)}`}>{statusLabel(pkg.status)}</span>
                  <span className="package-code">{pkg.access_code}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                    {(pkg.travel_timeline || []).length} itens
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detalhe */}
      <div className="vouchers-detail">
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="empty-state">
              <div className="empty-state-icon">👈</div>
              <div className="empty-state-text">Selecione um roteiro</div>
              <div className="empty-state-hint">ou crie um novo para começar</div>
            </div>
          </div>
        ) : (
          <>
            <div className="vouchers-detail-header">
              <div>
                <h2>{selected.title}</h2>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                  Código: <strong style={{ fontFamily: 'monospace', letterSpacing: 2 }}>{selected.access_code}</strong>
                  <span style={{ margin: '0 8px' }}>·</span>
                  <span className={`package-badge ${statusClass(selected.status)}`}>{statusLabel(selected.status)}</span>
                  {selected.start_date && (
                    <span style={{ marginLeft: 8 }}>
                      · {new Date(selected.start_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                      {selected.end_date && ` — ${new Date(selected.end_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.status === 'draft' && !publishedUrl && (
                  <button className="btn-publish" onClick={handlePublish} disabled={publishing}>
                    {publishing ? '⏳ Gerando...' : '🚀 Publicar'}
                  </button>
                )}
                {selected.status === 'published' && !publishedUrl && (
                  <button className="btn-publish" onClick={handlePublish} disabled={publishing} style={{ background: '#50cfad' }}>
                    {publishing ? '⏳ Gerando...' : '📤 Gerar link'}
                  </button>
                )}
                {publishedUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '6px 12px' }}>
                    <span style={{ fontSize: 12, color: '#166534', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {publishedUrl}
                    </span>
                    <button onClick={handleCopyLink} style={{ background: '#166534', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      📋 Copiar
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="vouchers-detail-content">
              <ClientSection pkg={selected} />

              {/* Timeline Extraída */}
              <div className="vouchers-section">
                <div className="vouchers-section-header">
                  <h3>📅 Timeline</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{(selected.travel_timeline || []).length} itens</span>
                    <button onClick={() => setAddingToPackageId(selected.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569', fontWeight: 600 }}>+ Vouchers</button>
                  </div>
                </div>
                <div className="vouchers-section-body">
                  {(selected.travel_timeline || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 20 }}>
                      Nenhum item na timeline.<br/>
                      <span style={{ fontSize: 12 }}>Adicione vouchers ao criar o roteiro — a IA extrai tudo automaticamente.</span>
                    </div>
                  ) : (
                    (selected.travel_timeline || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(item => (
                      <div key={item.id} className="tl-item">
                        <div className="tl-icon" style={{ background: `${item.color || '#e2e8f0'}20`, fontSize: 18 }}>{item.icon}</div>
                        <div className="tl-info">
                          <div className="tl-title">{item.title}</div>
                          <div className="tl-sub">{item.subtitle}</div>
                          {item.details?.booking_code && (
                            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>
                              LOC: {item.details.booking_code}
                            </div>
                          )}
                          {item.details?.operated_by && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Operado por {item.details.operated_by}</div>
                          )}
                          {item.details?.baggage && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>🧳 {item.details.baggage}</div>
                          )}
                          {item.details?.origin_code && item.details?.destination_code && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                              {item.details.origin_code} → {item.details.destination_code}
                              {item.details?.departure_time && item.details?.arrival_time && (
                                <span style={{ marginLeft: 8 }}>🕐 {item.details.departure_time} → {item.details.arrival_time}</span>
                              )}
                            </div>
                          )}
                          {item.details?.ticket_number && (
                            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 1 }}>
                              Bilhete: {item.details.ticket_number}
                            </div>
                          )}
                          {item.details?.room_type && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{item.details.room_type}{item.details.meal_plan ? ` · ${item.details.meal_plan}` : ''}</div>
                          )}
                          {item.details?.passengers && Array.isArray(item.details.passengers) && item.details.passengers.length > 0 && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                              👥 {item.details.passengers.map(p => p.name).join(', ')}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 60 }}>
                          <div className="tl-date">{item.date_label}</div>
                          <button onClick={() => handleDeleteTimelineItem(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#ef4444', marginTop: 4, padding: 0 }}>✕</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Passageiros */}
              <div className="vouchers-section">
                <div className="vouchers-section-header">
                  <h3>👥 Passageiros</h3>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{(selected.travel_passengers || []).length}</span>
                </div>
                <div className="vouchers-section-body">
                  {(selected.travel_passengers || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 12 }}>Nenhum passageiro</div>
                  ) : (
                    (selected.travel_passengers || []).map(p => (
                      <div key={p.id} className="pax-item">
                        <div className="pax-avatar">{p.initials || '?'}</div>
                        <div>
                          <div className="pax-name">{p.full_name}</div>
                          {p.document_number && <div className="pax-doc">{p.document_number}</div>}
                        </div>
                        {p.is_primary && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#50cfad' }}>TITULAR</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Documentos Originais */}
              <div className="vouchers-section">
                <div className="vouchers-section-header">
                  <h3>📎 Documentos Anexados</h3>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{(selected.travel_documents || []).length}</span>
                </div>
                <div className="vouchers-section-body">
                  {(selected.travel_documents || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 12 }}>Nenhum documento</div>
                  ) : (
                    (selected.travel_documents || []).map(doc => (
                      <div key={doc.id} className="doc-item">
                        <div className={`doc-icon ${doc.file_type}`}>{doc.file_type === 'pdf' ? '📕' : '🖼️'}</div>
                        <div className="doc-info">
                          <div className="doc-name">{doc.file_name}</div>
                          <div className="doc-meta">
                            {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString('pt-BR')}
                            <span style={{ marginLeft: 8, color: doc.extraction_status === 'extracted' ? '#10b981' : doc.extraction_status === 'failed' ? '#ef4444' : '#f59e0b' }}>
                              {doc.extraction_status === 'extracted' ? '✅ extraído' : doc.extraction_status === 'failed' ? '❌ falhou' : '⏳ pendente'}
                            </span>
                          </div>
                        </div>
                        <button className="doc-delete" onClick={() => handleDeleteDoc(doc)}>✕</button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal: Erro — contato sem cotação Won */}
      {wonQuoteError && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 400, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h3 style={{ marginBottom: 8, color: '#09077D' }}>Cotação não finalizada</h3>
            <p style={{ color: '#64748b', marginBottom: 24 }}>
              <strong>{wonQuoteError}</strong> não possui uma cotação marcada como <strong>Won</strong>. Finalize a venda antes de adicionar vouchers.
            </p>
            <button onClick={() => setWonQuoteError(null)} style={{ padding: '8px 24px', background: '#09077D', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal: Novo Roteiro (fluxo automatizado — vem do chat) */}
      {showNewFlow && (
        <NewVoucherFlow
          onClose={() => setShowNewFlow(false)}
          onCreated={handleNewCreated}
          initialContact={initialContact}
          initialWonQuote={initialWonQuote}
        />
      )}

      {/* Modal: Adicionar Vouchers a roteiro existente */}
      {addingToPackageId && (
        <NewVoucherFlow
          existingPackageId={addingToPackageId}
          onClose={() => setAddingToPackageId(null)}
          onCreated={async () => {
            setAddingToPackageId(null);
            await refreshSelected();
          }}
        />
      )}
    </div>
  );
}

// ─── Seção Cliente ───
function ClientSection({ pkg }) {
  const contact = pkg.contact, traveler = pkg.traveler;
  if (!contact && !traveler) {
    return (
      <div className="vouchers-section">
        <div className="vouchers-section-header"><h3>👤 Cliente</h3></div>
        <div className="vouchers-section-body">
          <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 12 }}>Nenhum cliente vinculado</div>
        </div>
      </div>
    );
  }
  const InfoRow = ({ label, value, mono }) => !value ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  );

  return (
    <div className="vouchers-section">
      <div className="vouchers-section-header">
        <h3>👤 Cliente</h3>
        {traveler && <span style={{ fontSize: 10, fontWeight: 700, color: '#50cfad', background: '#f0fdf4', padding: '3px 8px', borderRadius: 6 }}>VIAJANTE CRIADO</span>}
      </div>
      <div className="vouchers-section-body">
        <a
          href={contact?.id ? `/app/conversas?contact_id=${contact.id}` : undefined}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '0.5rem', borderRadius: 8, transition: 'background 0.15s', cursor: contact?.id ? 'pointer' : 'default' }}
          onMouseEnter={e => { if (contact?.id) e.currentTarget.style.background = '#f0f4ff' }}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title={contact?.id ? 'Ir para a conversa' : undefined}
        >
          {contact?.avatar_url ? (
            <img
              src={contact.avatar_url}
              alt=""
              style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
            />
          ) : null}
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#09077D', color: 'white', display: contact?.avatar_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
            {(contact?.name || traveler?.full_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#09077D', display: 'flex', alignItems: 'center', gap: 4 }}>
              {contact?.display_name || contact?.name || traveler?.full_name}
              {contact?.id && <span style={{ fontSize: 12, color: '#50CFAD' }}>↗</span>}
            </div>
            {contact?.phone && <div style={{ fontSize: 13, color: '#64748b', fontFamily: 'monospace' }}>{contact.phone}</div>}
            {contact?.cidade && contact?.uf && <div style={{ fontSize: 12, color: '#64748b' }}>📍 {contact.cidade}/{contact.uf}</div>}
          </div>
        </a>
        <InfoRow label="Email" value={traveler?.email || contact?.email} />
        <InfoRow label="CPF" value={traveler?.cpf || contact?.cpf} mono />
      </div>
    </div>
  );
}


// ─── Fluxo Novo Roteiro: Cliente → Upload → Extração → Confirma ───

function NewVoucherFlow({ onClose, onCreated, existingPackageId, initialContact, initialWonQuote }) {
  const isAppending = !!existingPackageId;
  const hasInitialContact = !!initialContact;
  // Pula step 1 se: append em pacote existente OU veio do chat com contato resolvido
  const [step, setStep] = useState(isAppending || hasInitialContact ? 2 : 1);
  const [selectedContact, setSelectedContact] = useState(initialContact || null);
  const [wonQuote] = useState(initialWonQuote || null);
  const [email, setEmail] = useState(initialContact?.email || '');
  const [cpf, setCpf] = useState(initialContact?.cpf || '');
  const [phone, setPhone] = useState(initialContact?.celular || initialContact?.phone || '');
  const [files, setFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [correction, setCorrection] = useState('');
  const [refining, setRefining] = useState(false);
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // Contact search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      const results = await vouchersApi.searchContacts(searchQuery);
      setSearchResults(results);
      setShowResults(true);
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  useEffect(() => {
    function handleClick(e) { if (resultsRef.current && !resultsRef.current.contains(e.target)) setShowResults(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelectContact = (c) => {
    setSelectedContact(c);
    setSearchQuery(c.name);
    setShowResults(false);
    setEmail(c.email || '');
    setCpf(c.cpf || '');
    setPhone(c.celular || c.phone || '');
  };

  const formatCpf = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  const handleAddFiles = (newFiles) => {
    setFiles(prev => [...prev, ...Array.from(newFiles)]);
    setExtraction(null);
    setExtractionError(null);
  };

  const handleRemoveFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setExtraction(null);
  };

  // ─── EXTRAÇÃO AUTOMÁTICA ───
  const handleExtract = async () => {
    if (files.length === 0) return;
    setExtracting(true);
    setExtractionError(null);
    setExtraction(null);

    try {
      const result = await vouchersApi.extractVouchers(files);
      if (result.success) {
        setExtraction(result.data);
        setStep(3);
      } else {
        setExtractionError(result.error || 'Falha na extração');
      }
    } catch (e) {
      setExtractionError(e.message || 'Erro de conexão');
    } finally {
      setExtracting(false);
    }
  };

  // ─── REFINAMENTO ───
  const handleRefine = async () => {
    if (!correction.trim() || !extraction) return;
    setRefining(true);
    try {
      const result = await vouchersApi.refineExtraction(extraction, correction);
      if (result.success) {
        setExtraction(result.data);
        setCorrection('');
      }
    } catch (e) {
      console.error('Refine error:', e);
    }
    setRefining(false);
  };

  // ─── SALVAR TUDO ───
  const handleConfirm = async () => {
    if (!extraction) return;
    setSaving(true);

    try {
      let pkgId;

      if (isAppending) {
        // Append to existing package
        pkgId = existingPackageId;

        // Upload primeiro para obter file_paths, depois salva itens com document_files
        const appendFileMap = {};
        const appendDocIds = [];
        for (const file of files) {
          const doc = await vouchersApi.uploadDocument(pkgId, file);
          if (doc) {
            appendDocIds.push(doc.id);
            appendFileMap[file.name] = doc.file_path;
          }
        }
        await vouchersApi.saveExtractedItems(pkgId, extraction, appendFileMap);
        await vouchersApi.updateDocExtractionStatus(appendDocIds, 'extracted');
      } else {
        // Create new package
        let traveler_id = null;
        let customer_id = selectedContact?.id || null;

        if (selectedContact && email) {
          const contactUpdates = {};
          if (email && !selectedContact.email) contactUpdates.email = email;
          if (cpf && !selectedContact.cpf) contactUpdates.cpf = cpf;
          if (Object.keys(contactUpdates).length > 0) {
            await vouchersApi.updateContact(selectedContact.id, contactUpdates);
          }
          let traveler = await vouchersApi.findTravelerByContact(selectedContact.id);
          if (!traveler) {
            traveler = await vouchersApi.createTraveler({
              contact_id: selectedContact.id,
              full_name: selectedContact.name,
              email, phone: phone || selectedContact.phone,
              cpf: cpf || null,
            });
          }
          if (traveler) traveler_id = traveler.id;
        }

        const pkg = await vouchersApi.createPackage({
          title: extraction.package_title || 'Novo Roteiro',
          subtitle: extraction.package_subtitle || null,
          customer_id, traveler_id,
          quote_id: wonQuote?.quote_id || null,
          start_date: extraction.start_date || null,
          end_date: extraction.end_date || null,
          destinations: extraction.destinations || [],
        });

        if (!pkg) throw new Error('Falha ao criar pacote');
        pkgId = pkg.id;

        // Upload primeiro para obter file_paths, depois salva itens com document_files
        const fileMap = {};
        const docIds = [];
        for (const file of files) {
          const doc = await vouchersApi.uploadDocument(pkgId, file);
          if (doc) {
            docIds.push(doc.id);
            fileMap[file.name] = doc.file_path;
          }
        }
        await vouchersApi.saveExtractedItems(pkgId, extraction, fileMap);
        await vouchersApi.updateDocExtractionStatus(docIds, 'extracted');
      }

      onCreated(pkgId);
    } catch (e) {
      console.error('Save error:', e);
      setExtractionError('Erro ao salvar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const stepLabels = isAppending ? ['Vouchers', 'Conferir'] : ['Cliente', 'Vouchers', 'Conferir'];
  const stepOffset = isAppending ? 1 : 0; // for mapping visual step to actual step

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: step === 3 ? 680 : 520 }}>
        <h3>{isAppending ? 'Adicionar Vouchers' : 'Novo Roteiro'}</h3>

        {/* Steps */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {stepLabels.map((label, i) => {
            const actualStep = i + 1 + stepOffset;
            return (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 4, borderRadius: 2, background: step > actualStep ? '#50cfad' : step === actualStep ? '#3b82f6' : '#e2e8f0', marginBottom: 4, transition: 'background 0.3s' }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: step === actualStep ? '#3b82f6' : step > actualStep ? '#50cfad' : '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
              </div>
            );
          })}
        </div>

        {/* Step 1: Cliente */}
        {step === 1 && (
          <>
            <div className="form-group" style={{ position: 'relative' }} ref={resultsRef}>
              <label>Buscar cliente (opcional)</label>
              {selectedContact ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '2px solid #50cfad', borderRadius: 8, background: '#f0fdf4' }}>
                  {selectedContact.avatar_url ? <img src={selectedContact.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} /> : (
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#090777', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{selectedContact.name[0].toUpperCase()}</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{selectedContact.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {selectedContact.phone}{selectedContact.cidade && ` · ${selectedContact.cidade}/${selectedContact.uf}`}
                    </div>
                  </div>
                  <button onClick={() => { setSelectedContact(null); setSearchQuery(''); setEmail(''); setCpf(''); setPhone(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8' }}>✕</button>
                </div>
              ) : (
                <>
                  <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => searchResults.length > 0 && setShowResults(true)} placeholder="Nome ou telefone..." autoFocus />
                  {searching && <div style={{ position: 'absolute', right: 12, top: 38, fontSize: 12, color: '#94a3b8' }}>Buscando...</div>}
                  {showResults && searchResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'white', borderRadius: '0 0 10px 10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', maxHeight: 240, overflowY: 'auto' }}>
                      {searchResults.map(c => (
                        <div key={c.id} onClick={() => handleSelectContact(c)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                          {c.avatar_url ? <img src={c.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} /> : (
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#64748b' }}>{c.name[0]}</div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.phone || ''}{c.email && ` · ${c.email}`}</div>
                          </div>
                          {c.email && <span style={{ fontSize: 10, color: '#50cfad', fontWeight: 700 }}>✓ email</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedContact && (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginTop: 4, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Dados do Viajante</div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Email {!email && <span style={{ color: '#ef4444', fontSize: 10 }}>* login do app</span>}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@cliente.com" />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>CPF</label>
                    <input value={cpf} onChange={e => setCpf(formatCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Celular</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+5577999..." />
                  </div>
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" onClick={() => setStep(2)}>
                {selectedContact ? 'Próximo →' : 'Pular cliente →'}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Upload de vouchers */}
        {step === 2 && (
          <>
            <div style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
              Arraste todos os vouchers da viagem. A IA vai ler, classificar e montar a timeline automaticamente.
            </div>

            <div
              className={`upload-zone ${dragging ? 'dragging' : ''}`}
              style={{ minHeight: 120 }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleAddFiles(Array.from(e.dataTransfer.files)); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-zone-icon">📂</div>
              <div className="upload-zone-text">Arraste vouchers aqui</div>
              <div className="upload-zone-hint">PDFs, prints de confirmação, e-tickets, seguros...</div>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
                onChange={e => { const arr = Array.from(e.target.files); e.target.value = ''; handleAddFiles(arr); }} />
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
                  {files.length} arquivo{files.length > 1 ? 's' : ''} selecionado{files.length > 1 ? 's' : ''}
                </div>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>{f.type.includes('pdf') ? '📕' : '🖼️'}</span>
                    <span style={{ flex: 1, fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => handleRemoveFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {extractionError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginTop: 12, fontSize: 13, color: '#dc2626' }}>
                ❌ {extractionError}
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => isAppending ? onClose() : setStep(1)}>← Voltar</button>
              <button className="btn-primary" onClick={handleExtract} disabled={files.length === 0 || extracting}
                style={{ minWidth: 160 }}>
                {extracting ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="spinner" /> Extraindo...
                  </span>
                ) : `🤖 Extrair ${files.length} voucher${files.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}

        {/* Step 3: Review da extração */}
        {step === 3 && extraction && (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{extraction.package_title || 'Roteiro'}</div>
              {extraction.package_subtitle && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{extraction.package_subtitle}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#475569' }}>
                {extraction.start_date && <span>📅 {extraction.start_date} → {extraction.end_date}</span>}
                {extraction.destinations?.length > 0 && <span>📍 {extraction.destinations.join(', ')}</span>}
                {extraction.passengers?.length > 0 && <span>👥 {extraction.passengers.length} passageiro{extraction.passengers.length > 1 ? 's' : ''}</span>}
              </div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
              Timeline ({(extraction.timeline_items || []).length} itens)
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
              {(extraction.timeline_items || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 20, width: 32, textAlign: 'center', paddingTop: 2 }}>{item.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{item.subtitle}</div>
                    {item.details?.booking_code && (
                      <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8' }}>LOC: {item.details.booking_code}</span>
                    )}
                    {item.details?.origin_code && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {item.details.origin_code} → {item.details.destination_code}
                        {item.details?.departure_time && ` · ${item.details.departure_time} → ${item.details.arrival_time}`}
                      </div>
                    )}
                    {item.details?.baggage && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>🧳 {item.details.baggage}</div>
                    )}
                    {item.details?.operated_by && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Operado por {item.details.operated_by}</div>
                    )}
                    {item.details?.passengers && Array.isArray(item.details.passengers) && item.details.passengers.length > 0 && (
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                        👥 {item.details.passengers.map(p => p.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', minWidth: 50, textAlign: 'right' }}>{item.date_label}</span>
                </div>
              ))}
            </div>

            {/* Passageiros encontrados */}
            {extraction.passengers?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                  Passageiros encontrados
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {extraction.passengers.map((p, i) => (
                    <span key={i} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569' }}>
                      👤 {p.full_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Correção por chat */}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={correction}
                onChange={e => setCorrection(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRefine()}
                placeholder="Algo errado? Corrija em linguagem natural..."
                style={{ flex: 1 }}
                disabled={refining}
              />
              <button onClick={handleRefine} disabled={!correction.trim() || refining}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: (!correction.trim() || refining) ? 0.5 : 1 }}>
                {refining ? '...' : '↻ Corrigir'}
              </button>
            </div>

            {extractionError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginTop: 12, fontSize: 13, color: '#dc2626' }}>
                ❌ {extractionError}
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => { setStep(2); setExtraction(null); }}>← Voltar</button>
              <button className="btn-primary" onClick={handleConfirm} disabled={saving} style={{ minWidth: 180 }}>
                {saving ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span className="spinner" /> Salvando...</span>
                ) : isAppending ? '✅ Adicionar ao Roteiro' : '✅ Confirmar e Criar Roteiro'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
